import { iLineChartConfig } from "@components/LineChart/LineChart.helpers";

interface IPenetrationData {
    periodName: string;
    penetration: number;
}

interface IKeywordData {
    id: number;
    name: string;
    currentPenetration: number;
    fourYearTrend: number;
    predictedGrowth: number;
    penetrations: IPenetrationData[];
    quarterPenetrations: IPenetrationData[];
    predictions: IPenetrationData[];
    quarterPredictions: IPenetrationData[];
}

interface IApiResponse {
    topTrendCategoryId: number;
    keywords: IKeywordData[];
}

// Static API data for testing (since API not ready)
const STATIC_API_DATA: IApiResponse = {
    "topTrendCategoryId": 1,
    "keywords": [
        {
            "id": 19003,
            "name": "PISTACHIO MILK",
            "currentPenetration": 0.28806585,
            "fourYearTrend": 599.5679,
            "predictedGrowth": -44.708393,
            "penetrations": [
                { "periodName": "2017", "penetration": 0.12378791 },
                { "periodName": "2018", "penetration": 0.08238929 },
                { "periodName": "2019", "penetration": 0.08233841 },
                { "periodName": "2020", "penetration": 0.041177683 },
                { "periodName": "2021", "penetration": 0.061791968 },
                { "periodName": "2022", "penetration": 0.16457519 },
                { "periodName": "2023", "penetration": 0.16460906 },
                { "periodName": "2024", "penetration": 0.28806585 }
            ],
            "quarterPenetrations": [
                { "periodName": "2025-Q1", "penetration": 0.28806585 }
            ],
            "predictions": [
                { "periodName": "2026", "penetration": 0.20120965 },
                { "periodName": "2027", "penetration": 0.18834226 },
                { "periodName": "2028", "penetration": 0.1767985 },
                { "periodName": "2029", "penetration": 0.15927623 }
            ],
            "quarterPredictions": [
                { "periodName": "2025-Q2", "penetration": 0.26022568 },
                { "periodName": "2025-Q3", "penetration": 0.25608796 },
                { "periodName": "2025-Q4", "penetration": 0.24679548 }
            ]
        },
        {
            "id": 30971,
            "name": "MIKE'S HOT HONEY",
            "currentPenetration": 0.781893,
            "fourYearTrend": 279.7654,
            "predictedGrowth": 64.610306,
            "penetrations": [
                { "periodName": "2017", "penetration": 0.041262638 },
                { "periodName": "2018", "penetration": 0.061791968 },
                { "periodName": "2019", "penetration": 0.12350762 },
                { "periodName": "2020", "penetration": 0.18529956 },
                { "periodName": "2021", "penetration": 0.2883625 },
                { "periodName": "2022", "penetration": 0.57601315 },
                { "periodName": "2023", "penetration": 0.63786006 },
                { "periodName": "2024", "penetration": 0.67901236 }
            ],
            "quarterPenetrations": [
                { "periodName": "2025-Q1", "penetration": 0.781893 }
            ],
            "predictions": [
                { "periodName": "2026", "penetration": 0.9033426 },
                { "periodName": "2027", "penetration": 1.0667202 },
                { "periodName": "2028", "penetration": 1.2545645 },
                { "periodName": "2029", "penetration": 1.2870765 }
            ],
            "quarterPredictions": [
                { "periodName": "2025-Q2", "penetration": 0.74708855 },
                { "periodName": "2025-Q3", "penetration": 0.7772687 },
                { "periodName": "2025-Q4", "penetration": 0.8022601 }
            ]
        },
        {
            "id": 24598,
            "name": "UBE",
            "currentPenetration": 1.2345679,
            "fourYearTrend": 274.76852,
            "predictedGrowth": 106.339584,
            "penetrations": [
                { "periodName": "2017", "penetration": 0.24757582 },
                { "periodName": "2018", "penetration": 0.2677652 },
                { "periodName": "2019", "penetration": 0.22643062 },
                { "periodName": "2020", "penetration": 0.30883262 },
                { "periodName": "2021", "penetration": 0.39134914 },
                { "periodName": "2022", "penetration": 0.63772887 },
                { "periodName": "2023", "penetration": 0.65843624 },
                { "periodName": "2024", "penetration": 1.1728395 }
            ],
            "quarterPenetrations": [
                { "periodName": "2025-Q1", "penetration": 1.2345679 }
            ],
            "predictions": [
                { "periodName": "2026", "penetration": 1.6897962 },
                { "periodName": "2027", "penetration": 2.006176 },
                { "periodName": "2028", "penetration": 2.4030874 },
                { "periodName": "2029", "penetration": 2.5474021 }
            ],
            "quarterPredictions": [
                { "periodName": "2025-Q2", "penetration": 1.2581711 },
                { "periodName": "2025-Q3", "penetration": 1.3097404 },
                { "periodName": "2025-Q4", "penetration": 1.3752129 }
            ]
        }
    ]
};

const SERIES_COLORS = ["#00EBF5", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981"];

// Helper: Extract year from the period name (e.g., "2025-Q1" -> "2025")
function extractYear(periodName: string): string {
    return periodName.split('-')[0];
}

// Helper: Extract quarter from period name (e.g., "2025-Q1" -> "Q1")
function extractQuarter(periodName: string): string | null {
    const parts = periodName.split('-');
    return parts.length > 1 ? parts[1] : null;
}

function isActualData(
    period: IPenetrationData, 
    quarterPenetrations: IPenetrationData[]
): boolean {
    return quarterPenetrations.some(q => q.periodName === period.periodName);
}

function buildTimePointsForYear(
    year: string,
    keyword: IKeywordData,
    allQuarterlyData: IPenetrationData[]
): any {
    const quarterlyDataForYear = allQuarterlyData.filter(q => extractYear(q.periodName) === year);
    
    if (quarterlyDataForYear.length > 0) {
        // Year has quarterly data - build sub-periods
        const sortedQuarters = quarterlyDataForYear.sort((a, b) => a.periodName.localeCompare(b.periodName));
        
        const points = sortedQuarters.map(quarter => ({
            value: quarter.penetration * 100,
            label: extractQuarter(quarter.periodName),
            isActual: isActualData(quarter, keyword.quarterPenetrations)
        }));

        return { label: year, points };
    } else {
        // Year has single point - find from yearly data
        const yearlyData = [...keyword.penetrations, ...keyword.predictions]
            .find(p => p.periodName === year);
        
        if (yearlyData) {
            const isDashed = keyword.predictions.some(p => p.periodName === year);
            return {
                label: year,
                points: [{ value: yearlyData.penetration * 100 }],
                isDashed
            };
        }
    }
    
    return null;
}

function transformApiDataToLineChart(apiData: IApiResponse): iLineChartConfig {
    const series = apiData.keywords.map((keyword, index) => {
        // Combine all quarterly data (actual + predicted)
        const allQuarterlyData = [...keyword.quarterPenetrations, ...keyword.quarterPredictions];
        
        // Get all unique years from all data sources
        const allYears = new Set<string>();
        keyword.penetrations.forEach(p => allYears.add(p.periodName));
        keyword.predictions.forEach(p => allYears.add(p.periodName));
        allQuarterlyData.forEach(q => allYears.add(extractYear(q.periodName)));
        
        const sortedYears = Array.from(allYears).sort();
        
        // Build data for each year
        const data = sortedYears
            .map(year => buildTimePointsForYear(year, keyword, allQuarterlyData))
            .filter(Boolean); // Remove null entries

        return {
            id: keyword.id.toString(),
            title: keyword.name.toUpperCase(),
            color: SERIES_COLORS[index % SERIES_COLORS.length],
            isActive: true,
            data
        };
    });

    const dashedLineStartIndex = series[0]?.data.findIndex(timePoint => timePoint.isDashed) ?? -1;

    return {
        series,
        showTooltip: true,
        dashedLineStartIndex,
        xAxisLabel: "Year",
        yAxisLabel: "Menu Penetration (%)"
    };
}

export function useLineChartData() {
    // For now, return static data but will be replaced with API data
    return {
        data: transformApiDataToLineChart(STATIC_API_DATA),
        isLoading: false,
        error: null
    };
}
